import Keycloak from "keycloak-js";
import {
  KEYCLOAK_URL,
  KEYCLOAK_REALM,
  KEYCLOAK_CLIENT_ID,
  KEYCLOAK_ROLE_MASTER
} from "./env";

const keycloak = new Keycloak({
  url: KEYCLOAK_URL,
  realm: KEYCLOAK_REALM,
  clientId: KEYCLOAK_CLIENT_ID
});

export const authRequired =
  KEYCLOAK_URL && KEYCLOAK_REALM && KEYCLOAK_CLIENT_ID ? true : false;
export const { login } = keycloak;
export function createLogoutUrl(redirectUri = window.location.origin) {
  const url = new URL(keycloak.createLogoutUrl({ redirectUri }));
  url.searchParams.delete("redirect_uri");
  url.searchParams.set("post_logout_redirect_uri", redirectUri);
  url.searchParams.set("client_id", KEYCLOAK_CLIENT_ID);
  if (keycloak.idToken) url.searchParams.set("id_token_hint", keycloak.idToken);
  return url.toString();
}
export function logout(options = {}) {
  window.location.assign(createLogoutUrl(options.redirectUri));
}
export const getToken = () => keycloak?.token;
let renovacao;
let sessaoExpirada = false;
let acessoNegadoPeloServico = false;
const notificarSessao = () =>
  window.dispatchEvent(new Event("esocial-session"));
export function getUser(token = keycloak.tokenParsed) {
  const text = (value) => (typeof value === "string" ? value.trim() : "");
  const username = text(token?.preferred_username);
  const name =
    text(token?.name) ||
    [text(token?.given_name), text(token?.family_name)]
      .filter(Boolean)
      .join(" ") ||
    username ||
    "Usuário autenticado";
  return { name, username };
}
export function hasMasterRole(token = keycloak.tokenParsed) {
  const roles = token?.realm_access?.roles;
  return (
    Boolean(KEYCLOAK_ROLE_MASTER) &&
    Array.isArray(roles) &&
    roles.includes(KEYCLOAK_ROLE_MASTER)
  );
}
export const getSessionStatus = () => ({
  authenticated: Boolean(keycloak.authenticated) && !sessaoExpirada,
  expiresAt: keycloak.tokenParsed?.exp,
  authRequired,
  hasAccess: Boolean(
    authRequired &&
      keycloak.authenticated &&
      !sessaoExpirada &&
      !acessoNegadoPeloServico &&
      hasMasterRole()
  ),
  user: keycloak.authenticated ? getUser() : null
});
export function negarAcesso() {
  if (acessoNegadoPeloServico) return;
  acessoNegadoPeloServico = true;
  notificarSessao();
}
export async function revalidarAcesso() {
  await updateToken(-1);
  acessoNegadoPeloServico = false;
  notificarSessao();
}
export function invalidarSessao() {
  if (sessaoExpirada) return;
  sessaoExpirada = true;
  notificarSessao();
}

export function updateToken(minValidity = 5) {
  if (!authRequired) return Promise.resolve(false);
  if (sessaoExpirada)
    return Promise.reject({
      status: 401,
      message: "Sua sessão expirou. Entre novamente para continuar."
    });
  if (!renovacao) {
    renovacao = Promise.resolve()
      .then(() => keycloak.updateToken(minValidity))
      .then((refreshed) => {
        if (!keycloak.token || keycloak.isTokenExpired(0))
          throw new Error("Sessão expirada");
        notificarSessao();
        return refreshed;
      })
      .catch(() => {
        invalidarSessao();
        throw Object.assign(
          new Error(
            "Não foi possível renovar sua sessão. Entre novamente para continuar."
          ),
          { status: 401 }
        );
      })
      .finally(() => {
        renovacao = undefined;
      });
  }
  return renovacao;
}

export function initKeycloak() {
  const configuradas = [
    KEYCLOAK_URL,
    KEYCLOAK_REALM,
    KEYCLOAK_CLIENT_ID
  ].filter(Boolean).length;
  if (configuradas > 0 && configuradas < 3)
    return Promise.reject(
      new Error("A configuração de autenticação está incompleta.")
    );
  if (!authRequired) {
    return Promise.resolve();
  }

  keycloak.onTokenExpired = () => updateToken(30).catch(() => {});
  keycloak.onAuthLogout = invalidarSessao;
  keycloak.onAuthRefreshSuccess = notificarSessao;
  return keycloak
    .init({
      onLoad: "login-required",
      pkceMethod: "S256"
    })
    .then((authenticated) => {
      if (!authenticated) {
        login();
      }
    })
    .catch((err) => {
      throw new Error(
        "Não foi possível iniciar a autenticação. Atualize a página para tentar novamente."
      );
    });
}
